import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Download, Check, X, Plus, Pencil, Eye, EyeOff, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getUser, setAuth, clearAuth, authFetch } from "@/lib/auth";
import CmsPanel from "@/pages/admin/CmsPanel";
import DiscoveryAdmin from "@/pages/admin/DiscoveryAdmin";
import ReportThemeSettings from "@/pages/admin/ReportThemeSettings";
import { UsersManager, ResultsManager } from "@/pages/admin/ManageTab";
import InstitutionsTab from "@/pages/admin/InstitutionsTab";
import { PERSONA_META } from "@/components/landing/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";
const TABS = [["overview", "Overview"], ["questions", "Questions"], ["results", "Results"], ["discovery", "Discovery"], ["users", "Users"], ["institutions", "Institutions"], ["pages", "Pages"], ["site", "Site"], ["design", "Design"], ["control", "Control Panel"]];

function Table({ title, cols, rows, testid }) {
  return (
    <div className="rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid={testid}>
      <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-sm tracking-tight">{title} <span className="text-ink/40 font-body font-medium">({rows.length})</span></div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-ink/45 uppercase tracking-wider">{cols.map((c) => <th key={c} className="px-6 py-3 font-semibold">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-ink/5 hover:bg-white/60 transition-colors">
                {r.map((cell, j) => <td key={j} className="px-6 py-3 whitespace-nowrap text-ink/75">{cell}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-6 py-6 text-ink/40">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { authFetch("/api/admin/stats").then((r) => r.json()).then(setStats).catch(() => toast.error("Stats failed")); }, []);
  if (!stats) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-iqblue" /></div>;
  const maxP = Math.max(1, ...Object.values(stats.personaDist));
  const maxDay = Math.max(1, ...stats.dailySignups.map((d) => d.count));
  const cards = [[stats.totalUsers, "Total users"], [stats.testsTaken, "Tests taken"], [stats.questions, "Active questions"]];
  return (
    <div className="space-y-6" data-testid="tab-overview">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map(([v, l]) => (
          <div key={l} className="rounded-3xl bg-cream border border-ink/10 p-6" data-testid={`stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-display font-bold text-3xl">{v}</div>
            <div className="text-xs text-ink/50 mt-1">{l}</div>
          </div>
        ))}
      </div>
      <div className="rounded-3xl bg-cream border border-ink/10 p-6" data-testid="persona-dist">
        <div className="font-display font-bold text-sm tracking-tight mb-5">Persona type distribution</div>
        <div className="space-y-2.5">
          {Object.entries(stats.personaDist).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="w-28 text-xs font-semibold text-ink/70 text-right">{PERSONA_META[k]?.en.name || k}</span>
              <div className="flex-1 h-6 rounded-lg bg-ink/5 overflow-hidden">
                <div className="h-full rounded-lg transition-all duration-700" style={{ width: `${(n / maxP) * 100}%`, background: PERSONA_META[k]?.color || "#1F5BFF" }} />
              </div>
              <span className="w-10 text-xs font-bold text-ink/60 text-right">{n}</span>
            </div>
          ))}
          {Object.keys(stats.personaDist).length === 0 && <div className="text-xs text-ink/40">No results yet.</div>}
        </div>
      </div>
      <div className="rounded-3xl bg-cream border border-ink/10 p-6" data-testid="daily-signups">
        <div className="font-display font-bold text-sm tracking-tight mb-5">Daily sign-ups (last 14 days)</div>
        <div className="flex items-end gap-1.5 h-28">
          {stats.dailySignups.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5">
              <span className="text-[9px] font-bold text-ink/50">{d.count || ""}</span>
              <div className="w-full rounded-t-md bg-gradient-to-t from-iqcyan to-iqblue transition-all duration-500" style={{ height: `${Math.max(3, (d.count / maxDay) * 100)}%` }} />
              <span className="text-[8px] text-ink/40">{d.day.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QForm({ q, onSave, onCancel, busy }) {
  const isApt = q.part === "apt";
  const [form, setForm] = useState({
    q: q.q, q_hi: q.q_hi || "",
    opts: (isApt ? q.opts : q.opts.map((o) => o.t)).join("\n"),
    opts_hi: (isApt ? (q.opts_hi || q.opts) : q.opts.map((o) => o.t_hi || o.t)).join("\n"),
    sub: q.sub || "logical", diff: q.diff || 2, ans: q.ans ?? 0,
  });
  const save = (e) => {
    e.preventDefault();
    const lines = form.opts.split("\n").map((s) => s.trim()).filter(Boolean);
    const linesHi = form.opts_hi.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload = { q: form.q, q_hi: form.q_hi, active: q.active !== false };
    if (isApt) Object.assign(payload, { opts: lines, opts_hi: linesHi, ans: Number(form.ans), sub: form.sub, diff: Number(form.diff) });
    else payload.opts = lines.map((t, i) => ({ ...(q.opts[i] || {}), t, t_hi: linesHi[i] || t }));
    onSave(payload);
  };
  return (
    <form onSubmit={save} className="mt-4 space-y-3 rounded-2xl border-2 border-iqblue/30 bg-white p-5" data-testid="q-form">
      <textarea value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} placeholder="Question (English)" required rows={2} className={inputCls} data-testid="q-text" />
      <textarea value={form.q_hi} onChange={(e) => setForm({ ...form, q_hi: e.target.value })} placeholder="Question (Hindi, optional)" rows={2} className={inputCls} data-testid="q-text-hi" />
      <textarea value={form.opts} onChange={(e) => setForm({ ...form, opts: e.target.value })} placeholder="Options — one per line" required rows={4} className={`${inputCls} font-mono text-xs`} data-testid="q-opts" />
      {isApt && (
        <div className="flex flex-wrap gap-3 items-center">
          <select value={form.sub} onChange={(e) => setForm({ ...form, sub: e.target.value })} className={`${inputCls} w-auto`} data-testid="q-sub">
            {["logical", "numerical", "verbal", "spatial"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={form.diff} onChange={(e) => setForm({ ...form, diff: e.target.value })} className={`${inputCls} w-auto`} data-testid="q-diff">
            {[1, 2, 3].map((d) => <option key={d} value={d}>difficulty {d}</option>)}
          </select>
          <select value={form.ans} onChange={(e) => setForm({ ...form, ans: e.target.value })} className={`${inputCls} w-auto`} data-testid="q-ans">
            {form.opts.split("\n").filter((s) => s.trim()).map((_, i) => <option key={i} value={i}>correct: option {i + 1}</option>)}
          </select>
        </div>
      )}
      <div className="flex gap-2.5">
        <button type="submit" disabled={busy} data-testid="q-save" className="flex items-center gap-1.5 bg-ink text-paper text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
        </button>
        <button type="button" onClick={onCancel} className="flex items-center gap-1.5 border border-ink/15 text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-ink hover:text-paper transition-colors">
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
      </div>
    </form>
  );
}

function QuestionsTab() {
  const [track, setTrack] = useState("student");
  const [qs, setQs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await authFetch(`/api/admin/questions?track=${track}`);
    if (res.ok) setQs(await res.json());
  };
  useEffect(() => { setQs(null); setEditing(null); load(); }, [track]);

  const save = async (payload) => {
    setBusy(true);
    const isNew = editing === "new";
    const res = await authFetch(isNew ? "/api/admin/questions" : `/api/admin/questions/${editing}`, {
      method: isNew ? "POST" : "PUT",
      body: JSON.stringify({ data: isNew ? { ...payload, track, part: "apt", q: payload.q, opts: payload.opts } : payload }),
    });
    if (!res.ok) toast.error("Save failed");
    else { toast.success("Question saved"); setEditing(null); await load(); }
    setBusy(false);
  };

  const toggle = async (q) => {
    await authFetch(`/api/admin/questions/${q.qid}`, { method: "PUT", body: JSON.stringify({ data: { active: q.active === false } }) });
    await load();
  };

  return (
    <div data-testid="tab-questions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-ink/15 p-1">
          {["student", "professional"].map((tr) => (
            <button key={tr} onClick={() => setTrack(tr)} data-testid={`q-track-${tr}`} className={`px-5 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${track === tr ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"}`}>{tr}</button>
          ))}
        </div>
        <span className="text-xs text-ink/45">{qs?.length ?? "…"} questions · preference options keep their persona mapping when you edit text</span>
      </div>
      {!qs && <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-iqblue" /></div>}
      {qs && (
        <div className="mt-6 space-y-3">
          {qs.map((q) => (
            <div key={q.qid} className={`rounded-2xl bg-cream border border-ink/10 p-5 ${q.active === false ? "opacity-50" : ""}`} data-testid={`q-item-${q.qid}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-ink/45">
                    <span className={`px-2 py-0.5 rounded-full ${q.part === "apt" ? "bg-iqblue/10 text-iqblue" : "bg-iqpink/10 text-iqpink"}`}>{q.part === "apt" ? "aptitude" : "preference"}</span>
                    {q.sub && <span>{q.sub}</span>}
                    {q.diff && <span>· level {q.diff}</span>}
                    <span>· {q.qid}</span>
                  </div>
                  <div className="font-display font-semibold text-sm tracking-tight mt-1.5">{q.q}</div>
                  <div className="text-xs text-ink/50 mt-1 truncate">{(q.part === "apt" ? q.opts : q.opts.map((o) => o.t)).join(" · ")}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => toggle(q)} data-testid={`q-toggle-${q.qid}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-paper transition-colors">
                    {q.active !== false ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => setEditing(editing === q.qid ? null : q.qid)} data-testid={`q-edit-${q.qid}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-iqblue hover:text-paper hover:border-iqblue transition-colors">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {editing === q.qid && <QForm q={q} onSave={save} onCancel={() => setEditing(null)} busy={busy} />}
            </div>
          ))}
          {editing === "new" ? (
            <div className="rounded-2xl bg-cream border border-ink/10 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-ink/45 mb-1">New aptitude question · {track}</div>
              <QForm q={{ part: "apt", q: "", opts: ["", "", "", ""], ans: 0, sub: "logical", diff: 2 }} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
            </div>
          ) : (
            <button onClick={() => setEditing("new")} data-testid="q-add" className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink/20 py-4 text-sm font-semibold text-ink/55 hover:border-iqblue hover:text-iqblue transition-colors">
              <Plus className="h-4 w-4" /> Add aptitude question ({track})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsForm({ section, renderFields, payload }) {
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    authFetch("/api/admin/settings").then((r) => r.json()).then((d) => { setS(d); setForm(payload(d)); });
  }, []);
  if (!form) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-iqblue" /></div>;
  const save = async () => {
    setBusy(true);
    const res = await authFetch("/api/admin/settings", { method: "PUT", body: JSON.stringify({ data: form }) });
    if (res.ok) toast.success("Saved — live on the site now");
    else toast.error("Save failed");
    setBusy(false);
  };
  return (
    <div className="rounded-3xl bg-cream border border-ink/10 p-6" data-testid={`settings-${section}`}>
      {renderFields(form, setForm)}
      <button onClick={save} disabled={busy} data-testid={`settings-save-${section}`} className="mt-5 flex items-center gap-1.5 bg-ink text-paper text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
      </button>
    </div>
  );
}

function SiteTab() {
  return (
    <div className="space-y-6" data-testid="tab-site">
      <SettingsForm
        section="hero"
        payload={(s) => ({ hero: { h1a: "", h1b: "", h1c: "", sub: "", ...(s.hero || {}) } })}
        renderFields={(form, setForm) => (
          <>
            <div className="font-display font-bold text-sm tracking-tight mb-4">Hero copy (English — Hindi stays built-in)</div>
            <div className="space-y-3">
              {[["h1a", "Headline line 1"], ["h1b", "Headline line 2 (before accent)"], ["h1c", "Accent word"]].map(([k, l]) => (
                <label key={k} className="block text-xs font-semibold text-ink/60">{l}
                  <input value={form.hero[k]} onChange={(e) => setForm({ ...form, hero: { ...form.hero, [k]: e.target.value } })} className={`${inputCls} mt-1`} data-testid={`site-hero-${k}`} />
                </label>
              ))}
              <label className="block text-xs font-semibold text-ink/60">Sub-heading
                <textarea value={form.hero.sub} onChange={(e) => setForm({ ...form, hero: { ...form.hero, sub: e.target.value } })} rows={3} className={`${inputCls} mt-1`} data-testid="site-hero-sub" />
              </label>
            </div>
          </>
        )}
      />
      <SettingsForm
        section="announcement"
        payload={(s) => ({ announcement: { text: "", active: false, ...(s.announcement || {}) } })}
        renderFields={(form, setForm) => (
          <>
            <div className="font-display font-bold text-sm tracking-tight mb-4">Announcement banner (top of landing page)</div>
            <input value={form.announcement.text} onChange={(e) => setForm({ ...form, announcement: { ...form.announcement, text: e.target.value } })} placeholder="e.g. Early access is free until 30 September" className={inputCls} data-testid="site-announcement-text" />
            <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-ink/60">
              <input type="checkbox" checked={form.announcement.active} onChange={(e) => setForm({ ...form, announcement: { ...form.announcement, active: e.target.checked } })} data-testid="site-announcement-active" /> show banner
            </label>
          </>
        )}
      />
    </div>
  );
}

function DesignTab() {
  const SECTIONS = [["marquee", "Editorial marquee"], ["manifesto", "Manifesto chapters"], ["spotlight", "Report spotlight"], ["steps", "Journey steps"], ["features", "Feature grid"], ["stories", "Testimonials"], ["faq", "FAQ"]];
  return (
    <div data-testid="tab-design" className="space-y-6">
      <ReportThemeSettings />
      <SettingsForm
        section="sections"
        payload={(s) => ({ sections: { marquee: true, manifesto: true, spotlight: true, steps: true, features: true, stories: true, faq: true, ...(s.sections || {}) } })}
        renderFields={(form, setForm) => (
          <>
            <div className="font-display font-bold text-sm tracking-tight mb-1">Landing page sections</div>
            <p className="text-xs text-ink/50 mb-4">Hidden sections disappear from the homepage immediately.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {SECTIONS.map(([k, l]) => (
                <label key={k} className="flex items-center justify-between rounded-2xl bg-white border border-ink/10 px-4 py-3.5 text-sm font-semibold" data-testid={`design-toggle-${k}`}>
                  {l}
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, sections: { ...form.sections, [k]: !form.sections[k] } })}
                    className={`relative h-6 w-11 rounded-full transition-colors duration-300 ${form.sections[k] ? "bg-iqgreen" : "bg-ink/20"}`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${form.sections[k] ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </label>
              ))}
            </div>
          </>
        )}
      />
    </div>
  );
}

function ControlTab({ downloadCsv }) {
  const [maintenance, setMaintenance] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { authFetch("/api/admin/settings").then((r) => r.json()).then((d) => setMaintenance(Boolean(d.maintenance))); }, []);

  const toggleMaintenance = async () => {
    const next = !maintenance;
    setMaintenance(next);
    await authFetch("/api/admin/settings", { method: "PUT", body: JSON.stringify({ data: { maintenance: next } }) });
    toast.success(next ? "Maintenance mode ON — assessments paused" : "Maintenance mode OFF");
  };

  const resend = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await authFetch("/api/admin/resend-report", { method: "POST", body: JSON.stringify({ email: resendEmail }) });
    const d = await res.json().catch(() => ({}));
    if (res.ok) toast.success(`Report resent to ${resendEmail}`);
    else toast.error(typeof d.detail === "string" ? d.detail : "Resend failed");
    setBusy(false);
  };

  const purge = async () => {
    if (!window.confirm("Delete ALL test users, reports, sessions, quiz submissions and early-access test entries? This cannot be undone.")) return;
    setBusy(true);
    const res = await authFetch("/api/admin/purge-test-data", { method: "POST" });
    const d = await res.json().catch(() => ({}));
    if (res.ok) toast.success(`Purged: ${Object.entries(d.deleted).map(([k, v]) => `${k}: ${v}`).join(", ")}`);
    else toast.error("Purge failed");
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="tab-control">
      <div className="rounded-3xl bg-cream border border-ink/10 p-6 flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-sm tracking-tight">Maintenance mode</div>
          <p className="text-xs text-ink/50 mt-1">Pauses quiz + battery submissions with a friendly message.</p>
        </div>
        {maintenance === null ? <Loader2 className="h-5 w-5 animate-spin text-iqblue" /> : (
          <button onClick={toggleMaintenance} data-testid="control-maintenance" className={`relative h-7 w-13 w-14 rounded-full transition-colors duration-300 ${maintenance ? "bg-iqpink" : "bg-ink/20"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${maintenance ? "left-8" : "left-1"}`} />
          </button>
        )}
      </div>

      <form onSubmit={resend} className="rounded-3xl bg-cream border border-ink/10 p-6">
        <div className="font-display font-bold text-sm tracking-tight">Resend report email</div>
        <p className="text-xs text-ink/50 mt-1 mb-4">Sends the member's latest full report again.</p>
        <div className="flex gap-3">
          <input type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="member@email.com" required className={inputCls} data-testid="control-resend-email" />
          <button disabled={busy} data-testid="control-resend-button" className="flex items-center gap-1.5 bg-ink text-paper text-xs font-semibold px-5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors shrink-0">
            <Send className="h-3.5 w-3.5" /> Resend
          </button>
        </div>
      </form>

      <div className="rounded-3xl bg-cream border border-ink/10 p-6">
        <div className="font-display font-bold text-sm tracking-tight mb-4">CSV exports</div>
        <div className="flex flex-wrap gap-2.5" data-testid="admin-export-bar">
          {[["users", "Users"], ["early-access", "Early access"], ["assessments", "Quiz submissions"], ["reports", "Full reports"], ["discovery", "Discovery"]].map(([kind, label]) => (
            <button key={kind} onClick={() => downloadCsv(kind)} data-testid={`admin-export-${kind}`} className="flex items-center gap-2 text-xs font-semibold border border-ink/15 rounded-full px-4 py-2 hover:bg-ink hover:text-paper transition-colors duration-300">
              <Download className="h-3.5 w-3.5" /> {label} CSV
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-iqpink/5 border border-iqpink/25 p-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-display font-bold text-sm tracking-tight flex items-center gap-2 text-iqpink"><AlertTriangle className="h-4 w-4" /> Clear test data</div>
          <p className="text-xs text-ink/55 mt-1">Removes all test users, their reports/sessions, quiz submissions and early-access test entries. Admin account is kept.</p>
        </div>
        <button onClick={purge} disabled={busy} data-testid="control-purge" className="shrink-0 text-xs font-bold bg-iqpink text-paper px-5 py-2.5 rounded-full hover:brightness-110 disabled:opacity-60 transition-all">
          Purge
        </button>
      </div>
    </div>
  );
}

export default function AdminPage({ initialTab = "overview" }) {
  const [user, setUser] = useState(getUser());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);

  const isAdmin = user?.role === "admin";

  const reload = () => authFetch("/api/admin/overview").then((r) => r.json()).then(setData).catch(() => toast.error("Failed to load admin data"));

  useEffect(() => {
    if (!isAdmin) return;
    reload();
  }, [isAdmin]);

  const downloadCsv = async (kind) => {
    try {
      const res = await authFetch(`/api/admin/export/${kind}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `brightiq-${kind}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded brightiq-${kind}.csv`);
    } catch {
      toast.error("Export failed");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const d = await res.json();
      if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : "Login failed");
      else if (d.user.role !== "admin") toast.error("This account is not an admin.");
      else {
        setAuth(d.token, d.user);
        setUser(d.user);
        toast.success("Welcome, admin");
      }
    } catch {
      toast.error("Network error");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="admin-page">
      <header className="max-w-6xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span> <span className="text-ink/35 font-body text-xs font-semibold tracking-[0.2em] uppercase ml-1">Admin Console</span></span>
        </Link>
        {isAdmin && (
          <button onClick={() => { clearAuth(); setUser(null); setData(null); }} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-xs font-semibold text-ink/50 hover:text-iqpink transition-colors">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        {!isAdmin && (
          <form onSubmit={login} className="max-w-sm mx-auto rounded-[2rem] bg-cream border border-ink/10 p-8" data-testid="admin-login-form">
            <h1 className="font-display font-bold text-2xl tracking-tight">Admin sign in</h1>
            <p className="mt-2 text-sm text-ink/55">Restricted to the Bright IQ team.</p>
            <div className="mt-6 space-y-3">
              <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@brightiq.co" required className={inputCls} />
              <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className={inputCls} />
              <button type="submit" disabled={busy} data-testid="admin-login-button" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-3.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </button>
            </div>
          </form>
        )}

        {isAdmin && (
          <>
            <h1 className="font-display font-bold text-3xl tracking-tight">Admin Console</h1>
            <p className="mt-1.5 text-sm text-ink/55">Manage the assessment and monitor results.</p>
            <div className="mt-6 flex flex-wrap gap-1.5 border-b border-ink/10 pb-0" data-testid="admin-tabs">
              {TABS.map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`px-4 py-2.5 text-sm font-semibold rounded-t-xl transition-colors duration-300 border-b-2 -mb-px ${tab === k ? "text-iqblue border-iqblue bg-iqblue/5" : "text-ink/55 border-transparent hover:text-ink"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="pt-8">
              {tab === "overview" && <OverviewTab />}
              {tab === "questions" && <QuestionsTab />}
              {tab === "discovery" && <DiscoveryAdmin />}
              {tab === "institutions" && <InstitutionsTab />}
              {tab === "pages" && <CmsPanel />}
              {tab === "site" && <SiteTab />}
              {tab === "design" && <DesignTab />}
              {tab === "control" && <ControlTab downloadCsv={downloadCsv} />}
              {(tab === "results" || tab === "users") && !data && <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-iqblue" /></div>}
              {tab === "results" && data && (
                <div className="space-y-6" data-testid="tab-results">
                  <ResultsManager reports={data.full_reports} onChanged={reload} />
                  <Table title="Teaser quiz submissions" testid="admin-table-quiz" cols={["Name", "Email", "Grade", "Persona", "When"]}
                    rows={data.assessments.map((r) => [r.name, r.email, r.grade || "—", r.top || "—", fmtDate(r.timestamp)])} />
                </div>
              )}
              {tab === "users" && data && (
                <div className="space-y-6" data-testid="tab-users">
                  <UsersManager users={data.users} onChanged={reload} />
                  <Table title="Early access list" testid="admin-table-early" cols={["Email", "Name", "When"]}
                    rows={data.early_access.map((r) => [r.email, r.name || "—", fmtDate(r.timestamp)])} />
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
